import "./MetaDocEmptyScreen.scss";

import Button from "components/Button/Button";
import KaboodleToaster from "components/common/KaboodleToaster";
import type { DocumentType } from "components/Documents/DocumentTypes";
import DeleteConfirmationModal from "components/Documents/shared/DeleteConfirmationModal";
import MetaDocTable from "components/Documents/table/MetaDocTable";
import LoadingIcon from "components/Loading/LoadingIcon";
import { useMemo, useState } from "react";
import type { Metadoc } from "services/metadoc-api";
import { useDeleteMetadocMutation } from "services/metadoc-api";

import * as Sentry from "@sentry/react";

type MetaDocEmptyScreenProps = {
  onCreateNew: () => void;
  metadocs: Metadoc[];
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  fileId: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  fileServices?: Array<{
    id: number;
    type: string;
    type_verbose: string;
    label: string;
  }>;
};

type DeleteModalState = {
  isOpen: boolean;
  document: DocumentType | null;
};

const MetaDocEmptyScreen = ({
  onCreateNew,
  metadocs,
  totalCount,
  page,
  onPageChange,
  isLoading,
  fileId,
  searchValue,
  onSearchChange,
  fileServices = [],
}: MetaDocEmptyScreenProps) => {
  const [deleteMetadoc] = useDeleteMetadocMutation();
  const [expandedMetadocIds, setExpandedMetadocIds] = useState<Set<number>>(
    new Set(),
  );
  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isOpen: false,
    document: null,
  });

  const handleToggleExpansion = (metadocId: number) => {
    setExpandedMetadocIds((prev) => {
      const next = new Set(prev);
      if (next.has(metadocId)) {
        next.delete(metadocId);
      } else {
        next.add(metadocId);
      }
      return next;
    });
  };

  const handleDocumentDelete = (metadoc: Metadoc) => {
    const docForModal = {
      id: metadoc.id,
      original_filename: metadoc.name,
    } as DocumentType;

    setDeleteModalState({ isOpen: true, document: docForModal });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.document) return;

    try {
      await deleteMetadoc(deleteModalState.document.id).unwrap();
      setDeleteModalState({ isOpen: false, document: null });
      KaboodleToaster.success("Meta Document deleted successfully");
    } catch (error) {
      // log to Sentry for monitoring
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: "metadoc_deletion" },
        extra: { metadocId: deleteModalState.document?.id },
      });
      KaboodleToaster.error("Failed to delete Meta Document");
    }
  };

  // transform metadocs into flat table rows with expanded children
  const tableRows = useMemo(() => {
    const rows: Array<
      Metadoc & { isChild?: boolean; parentMetadocId?: number }
    > = [];

    metadocs.forEach((metadoc) => {
      // add the metadoc itself
      rows.push(metadoc);

      // if expanded, add its source documents as children
      if (expandedMetadocIds.has(metadoc.id) && metadoc.source_documents) {
        metadoc.source_documents.forEach((sourceDoc) => {
          rows.push({
            ...metadoc,
            id: sourceDoc.id,
            name: sourceDoc.name,
            page_count: sourceDoc.page_count,
            file_size_bytes: sourceDoc.file_size_bytes,
            created: sourceDoc.created,
            isChild: true,
            parentMetadocId: metadoc.id,
            // keep metadoc-specific fields for the parent context
            status: metadoc.status,
            source_documents: metadoc.source_documents,
            download_url: null, // source docs don't have download URLs in this context
            error_message: null,
            description: metadoc.description,
            is_linearized: metadoc.is_linearized,
            created_by: metadoc.created_by,
          });
        });
      }
    });

    return rows;
  }, [metadocs, expandedMetadocIds]);

  const renderContent = () => {
    const hasSearchValue = searchValue.trim().length > 0;

    // show loading state
    if (isLoading) {
      return (
        <div className="metadoc-dashboard-loading">
          <LoadingIcon message="Loading Meta Documents" />
        </div>
      );
    }

    // show empty state
    if (metadocs.length === 0 && !hasSearchValue) {
      return (
        <div className="metadoc-empty-state">
          <span className="material-icons metadoc-empty-icon">description</span>
          <p className="metadoc-empty-message">
            No meta documents yet. Create your first one by selecting source
            documents.
          </p>
          <Button className="allan-koba" onClick={onCreateNew}>
            <span className="material-icons">add</span>
            Create New Meta Document
          </Button>
        </div>
      );
    }

    // render table
    return (
      <MetaDocTable
        documents={tableRows}
        totalCount={totalCount}
        page={page}
        onPageChange={onPageChange}
        onDelete={handleDocumentDelete}
        isLoading={isLoading}
        isDrawer={false}
        fileServices={fileServices}
        fileId={fileId}
        onToggleExpansion={handleToggleExpansion}
        expandedIds={expandedMetadocIds}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        emptyTableMessage="No Meta Documents match your search"
      />
    );
  };

  return (
    <>
      <div className="metadoc-dashboard-container">
        <div className="metadoc-dashboard-header">
          <div className="metadoc-dashboard-header-title">Meta Documents</div>
          <div className="metadoc-dashboard-header-actions">
            {metadocs.length > 0 && (
              <button className="metadoc-create-button" onClick={onCreateNew}>
                <span className="material-icons">add</span>
                Create New Meta Document
              </button>
            )}
          </div>
        </div>
        <div className="metadoc-dashboard-content">{renderContent()}</div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalState.isOpen}
        onClose={() => setDeleteModalState({ isOpen: false, document: null })}
        onConfirm={handleConfirmDelete}
        fileName={deleteModalState.document?.original_filename || ""}
      />
    </>
  );
};

export default MetaDocEmptyScreen;
